import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { OrdersTab, CustomersTab, NotificationsTab, DiscountsTab } from "@/pages/AdminTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, Upload, Plus, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["rings", "necklaces", "earrings", "bracelets", "pendants"];

const MODELS = {
  anthropic: ["claude-sonnet-4-5-20250929", "claude-sonnet-4-6", "claude-opus-4-7", "claude-haiku-4-5-20251001"],
  openai: ["gpt-5.4", "gpt-5.2", "gpt-4o", "gpt-4o-mini"],
  gemini: ["gemini-3-flash-preview", "gemini-3.1-pro-preview", "gemini-2.5-flash"],
};

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    category: "rings",
    price: 0,
    description: "",
    material: "",
    warranty: "",
    stock: 10,
    featured: false,
  });
  const fileRef = useRef(null);

  const reload = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => {
    reload();
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      category: "rings",
      price: 0,
      description: "",
      material: "",
      warranty: "",
      stock: 10,
      featured: false,
    });
    setOpen(true);
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      price: p.price,
      description: p.description,
      material: p.material || "",
      warranty: p.warranty || "",
      stock: p.stock || 0,
      featured: !!p.featured,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", { ...form, images: [] });
        toast.success("Product created");
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    reload();
  };

  const uploadImages = async (productId, files) => {
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/products/${productId}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        toast.error(`Upload failed: ${e.response?.data?.detail || f.name}`);
      }
    }
    toast.success("Images uploaded");
    reload();
  };

  const removeImage = async (productId, path) => {
    await api.delete(`/products/${productId}/images`, { params: { path } });
    toast.success("Image removed");
    reload();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-serif text-3xl">Products</h2>
          <p className="text-sm text-[#5c5a56] mt-1">{products.length} pieces in the atelier</p>
        </div>
        <Button onClick={startCreate} className="rounded-none" data-testid="new-product-btn">
          <Plus size={14} className="mr-1.5" /> New product
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Stock</TableHead>
            <TableHead>Images</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((p) => (
            <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#f3efea] overflow-hidden">
                    {p.images?.[0] && (
                      <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#5c5a56]">{p.material}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="capitalize">{p.category}</TableCell>
              <TableCell>₹{p.price?.toLocaleString("en-IN")}</TableCell>
              <TableCell>{p.stock}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 flex-wrap max-w-[280px]">
                  {(p.images || []).map((img, idx) => (
                    <div key={idx} className="relative w-10 h-10 bg-[#f3efea]">
                      <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeImage(p.id, img)}
                        className="absolute -top-1.5 -right-1.5 bg-[#8b3a36] text-white rounded-full w-4 h-4 flex items-center justify-center"
                        data-testid={`remove-img-${p.id}-${idx}`}
                        title="Remove"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  <label className="w-10 h-10 border border-dashed border-[#c58b76] flex items-center justify-center cursor-pointer hover:bg-[#f3efea]">
                    <Upload size={14} className="text-[#c58b76]" />
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      data-testid={`upload-${p.id}`}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length) uploadImages(p.id, files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <button
                  onClick={() => startEdit(p)}
                  className="text-xs uppercase tracking-wider text-[#1a1918] mr-3 hover:text-[#c58b76]"
                  data-testid={`edit-${p.id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => removeProduct(p.id)}
                  className="text-[#8b3a36] hover:text-[#1a1918]"
                  data-testid={`delete-${p.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit piece" : "New piece"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid="product-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (₹)</Label>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  data-testid="product-form-price"
                />
              </div>
            </div>
            <div>
              <Label>Material</Label>
              <Input
                value={form.material}
                onChange={(e) => setForm({ ...form, material: e.target.value })}
                data-testid="product-form-material"
              />
            </div>
            <div>
              <Label>Warranty (shown to customer)</Label>
              <Input
                value={form.warranty}
                onChange={(e) => setForm({ ...form, warranty: e.target.value })}
                placeholder="e.g. 6 months warranty, Lifetime warranty…"
                data-testid="product-form-warranty"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                data-testid="product-form-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-end gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                />
                <Label htmlFor="featured">Featured</Label>
              </div>
            </div>
            <Button onClick={save} className="w-full rounded-none" data-testid="product-form-save">
              {editing ? "Save changes" : "Create product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "support" });

  const reload = () => api.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "support" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    await api.delete(`/admin/staff/${id}`);
    reload();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-serif text-3xl">Staff</h2>
          <p className="text-sm text-[#5c5a56] mt-1">Admins and support stylists</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-none" data-testid="add-staff-btn">
          <Plus size={14} className="mr-1.5" /> Add staff
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {staff.map((s) => (
            <TableRow key={s.id}>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.email}</TableCell>
              <TableCell className="capitalize">{s.role}</TableCell>
              <TableCell className="text-right">
                <button onClick={() => remove(s.id)} className="text-[#8b3a36]" data-testid={`remove-staff-${s.id}`}>
                  <Trash2 size={16} />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New staff member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="staff-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={create} className="w-full rounded-none" data-testid="staff-create">
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChatConfigTab() {
  const [cfg, setCfg] = useState({ provider: "anthropic", model: "claude-sonnet-4-5-20250929" });
  useEffect(() => {
    api.get("/admin/chat-config").then((r) => setCfg({ provider: r.data.provider, model: r.data.model }));
  }, []);

  const save = async () => {
    await api.put("/admin/chat-config", cfg);
    toast.success("Chat model updated");
  };

  return (
    <div className="max-w-lg">
      <h2 className="font-serif text-3xl mb-2">AI Chatbot</h2>
      <p className="text-sm text-[#5c5a56] mb-8">
        Choose which model powers Konoka, the customer concierge.
      </p>
      <div className="space-y-4">
        <div>
          <Label>Provider</Label>
          <Select
            value={cfg.provider}
            onValueChange={(v) =>
              setCfg({ provider: v, model: MODELS[v][0] })
            }
          >
            <SelectTrigger data-testid="chat-provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
              <SelectItem value="openai">OpenAI (GPT)</SelectItem>
              <SelectItem value="gemini">Google (Gemini)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Model</Label>
          <Select value={cfg.model} onValueChange={(v) => setCfg({ ...cfg, model: v })}>
            <SelectTrigger data-testid="chat-model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODELS[cfg.provider].map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} className="rounded-none" data-testid="save-chat-config">
          Save settings
        </Button>
      </div>
    </div>
  );
}

function ContentTab() {
  const { content, reload } = useSiteContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // {done, total}
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (content && !draft) setDraft(content);
  }, [content]); // eslint-disable-line

  if (!draft) {
    return <div className="text-[#5c5a56]">Loading content…</div>;
  }

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const updateTitleLine = (idx, val) => {
    const next = [...(draft.hero_title_lines || [])];
    next[idx] = val;
    set("hero_title_lines", next);
  };
  const addTitleLine = () => set("hero_title_lines", [...(draft.hero_title_lines || []), ""]);
  const removeTitleLine = (idx) =>
    set("hero_title_lines", (draft.hero_title_lines || []).filter((_, i) => i !== idx));

  const updateStat = (idx, field, val) => {
    const next = [...(draft.hero_stats || [])];
    next[idx] = { ...next[idx], [field]: val };
    set("hero_stats", next);
  };
  const addStat = () => set("hero_stats", [...(draft.hero_stats || []), { value: "", label: "" }]);
  const removeStat = (idx) =>
    set("hero_stats", (draft.hero_stats || []).filter((_, i) => i !== idx));

  const updateMarquee = (idx, val) => {
    const next = [...(draft.marquee_items || [])];
    next[idx] = val;
    set("marquee_items", next);
  };
  const addMarquee = () => set("marquee_items", [...(draft.marquee_items || []), ""]);
  const removeMarquee = (idx) =>
    set("marquee_items", (draft.marquee_items || []).filter((_, i) => i !== idx));

  const updateStoryLine = (idx, val) => {
    const next = [...(draft.story_title_lines || [])];
    next[idx] = val;
    set("story_title_lines", next);
  };
  const addStoryLine = () =>
    set("story_title_lines", [...(draft.story_title_lines || []), ""]);
  const removeStoryLine = (idx) =>
    set("story_title_lines", (draft.story_title_lines || []).filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { slides, id, ...payload } = draft;
      await api.put("/site/content", payload);
      await reload();
      toast.success("Home content updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadSlide = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) {
      toast.error("Please select image files only");
      return;
    }
    setUploadStatus({ done: 0, total: list.length });
    let success = 0;
    let failed = 0;
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/site/slides", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        success++;
      } catch (e) {
        failed++;
        toast.error(`Upload failed: ${f.name}`);
      }
      setUploadStatus({ done: i + 1, total: list.length });
    }
    await reload();
    setUploadStatus(null);
    if (success > 0) {
      toast.success(
        `${success} slide${success === 1 ? "" : "s"} added${failed ? ` · ${failed} failed` : ""}`
      );
    }
  };

  const removeSlide = async (path) => {
    if (!window.confirm("Remove this slide?")) return;
    await api.delete("/site/slides", { params: { path } });
    await reload();
    toast.success("Slide removed");
  };

  // Pull the latest slides from the live content (not local draft, since uploads bypass draft)
  const slides = (content?.slides || []).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-12 max-w-4xl">
      {/* SLIDES */}
      <section>
        <div className="flex justify-between items-end mb-4">
          <div>
            <h2 className="font-serif text-3xl">Hero background slideshow</h2>
            <p className="text-sm text-[#5c5a56] mt-1">
              {slides.length === 0
                ? "Upload one or more images to enable the slideshow."
                : `${slides.length} slide${slides.length === 1 ? "" : "s"} · cross-fades every ${(draft.slide_interval_ms || 5500) / 1000}s · shown behind the entire home page.`}
            </p>
          </div>
        </div>

        {/* Dropzone (also a click target for file picker, supports multi-select) */}
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length) uploadSlide(files);
          }}
          className={`border-2 border-dashed ${
            isDragging
              ? "border-[#c58b76] bg-[#fdfbf7]"
              : "border-[#ebe5df] bg-[#fdfbf7]/60 hover:border-[#c58b76]"
          } p-10 text-center cursor-pointer transition-colors mb-6`}
          data-testid="slide-dropzone"
        >
          <Upload size={28} className="text-[#c58b76] mx-auto" strokeWidth={1.4} />
          <div className="font-serif text-xl mt-3">
            {uploadStatus
              ? `Uploading ${uploadStatus.done} of ${uploadStatus.total}…`
              : "Drop images here or click to browse"}
          </div>
          <div className="text-xs tracking-[0.16em] uppercase text-[#5c5a56] mt-2">
            JPG · PNG · WEBP · You can select multiple files at once
          </div>
          {uploadStatus && (
            <div className="mt-4 max-w-sm mx-auto h-1 bg-[#ebe5df] overflow-hidden">
              <div
                className="h-full bg-[#c58b76] transition-all"
                style={{
                  width: `${(uploadStatus.done / Math.max(1, uploadStatus.total)) * 100}%`,
                }}
              />
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            data-testid="slides-input"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) uploadSlide(files);
              e.target.value = "";
            }}
          />
        </div>

        {slides.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {slides.map((s, i) => (
              <div
                key={s.path}
                className="relative aspect-[4/3] bg-[#f3efea] overflow-hidden group"
                data-testid={`slide-${i}`}
              >
                <img src={fileUrl(s.path)} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeSlide(s.path)}
                  className="absolute top-2 right-2 bg-[#8b3a36] text-white w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-slide-${i}`}
                  title="Remove"
                >
                  <X size={14} />
                </button>
                <div className="absolute bottom-2 left-2 overline bg-white/90 px-1.5 py-0.5 text-[10px]">
                  Slide {i + 1}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mt-6 max-w-sm">
          <div>
            <Label>Slide interval (seconds)</Label>
            <Input
              type="number"
              min={2}
              value={(draft.slide_interval_ms || 5500) / 1000}
              onChange={(e) =>
                set("slide_interval_ms", Math.max(2, parseInt(e.target.value || "5") || 5) * 1000)
              }
              data-testid="slide-interval"
            />
          </div>
          <div className="flex items-end gap-2 pb-2">
            <input
              type="checkbox"
              id="show3d"
              checked={draft.show_hero_3d !== false}
              onChange={(e) => set("show_hero_3d", e.target.checked)}
              data-testid="show-hero-3d"
            />
            <Label htmlFor="show3d">Show 3D ring on hero</Label>
          </div>
        </div>
      </section>

      {/* HERO COPY */}
      <section>
        <h2 className="font-serif text-3xl mb-4">Hero section</h2>
        <div className="space-y-4">
          <div>
            <Label>Overline (small caption above title)</Label>
            <Input
              value={draft.hero_overline || ""}
              onChange={(e) => set("hero_overline", e.target.value)}
              data-testid="hero-overline"
            />
          </div>
          <div>
            <Label>Title lines (one per row)</Label>
            <div className="space-y-2">
              {(draft.hero_title_lines || []).map((line, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    value={line}
                    onChange={(e) => updateTitleLine(i, e.target.value)}
                    data-testid={`title-line-${i}`}
                  />
                  <input
                    type="radio"
                    name="accent"
                    checked={draft.hero_accent_line === i}
                    onChange={() => set("hero_accent_line", i)}
                    title="Accent (rose-gold) line"
                    data-testid={`accent-${i}`}
                  />
                  <button onClick={() => removeTitleLine(i)} className="text-[#8b3a36]">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button onClick={addTitleLine} className="text-xs uppercase tracking-widest text-[#c58b76]">
                + Add line
              </button>
              <div className="text-[11px] text-[#5c5a56]">
                Tick the radio to mark the rose-gold accent line.
              </div>
            </div>
          </div>
          <div>
            <Label>Subtitle</Label>
            <Textarea
              rows={3}
              value={draft.hero_subtitle || ""}
              onChange={(e) => set("hero_subtitle", e.target.value)}
              data-testid="hero-subtitle"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Primary CTA label</Label>
              <Input
                value={draft.hero_cta_primary || ""}
                onChange={(e) => set("hero_cta_primary", e.target.value)}
                data-testid="cta-primary"
              />
            </div>
            <div>
              <Label>Secondary CTA label</Label>
              <Input
                value={draft.hero_cta_secondary || ""}
                onChange={(e) => set("hero_cta_secondary", e.target.value)}
                data-testid="cta-secondary"
              />
            </div>
          </div>
          <div>
            <Label>Stats (shown under CTAs)</Label>
            <div className="space-y-2">
              {(draft.hero_stats || []).map((s, i) => (
                <div key={i} className="grid grid-cols-[140px_1fr_auto] gap-2">
                  <Input
                    placeholder="18k"
                    value={s.value}
                    onChange={(e) => updateStat(i, "value", e.target.value)}
                    data-testid={`stat-value-${i}`}
                  />
                  <Input
                    placeholder="Solid gold"
                    value={s.label}
                    onChange={(e) => updateStat(i, "label", e.target.value)}
                    data-testid={`stat-label-${i}`}
                  />
                  <button onClick={() => removeStat(i)} className="text-[#8b3a36]">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button onClick={addStat} className="text-xs uppercase tracking-widest text-[#c58b76]">
                + Add stat
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section>
        <h2 className="font-serif text-3xl mb-4">Marquee strip</h2>
        <div className="space-y-2">
          {(draft.marquee_items || []).map((t, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                value={t}
                onChange={(e) => updateMarquee(i, e.target.value)}
                data-testid={`marquee-${i}`}
              />
              <button onClick={() => removeMarquee(i)} className="text-[#8b3a36]">
                <X size={16} />
              </button>
            </div>
          ))}
          <button onClick={addMarquee} className="text-xs uppercase tracking-widest text-[#c58b76]">
            + Add item
          </button>
        </div>
      </section>

      {/* FEATURED */}
      <section>
        <h2 className="font-serif text-3xl mb-4">Featured products section</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Overline</Label>
            <Input
              value={draft.featured_overline || ""}
              onChange={(e) => set("featured_overline", e.target.value)}
              data-testid="featured-overline"
            />
          </div>
          <div>
            <Label>Title</Label>
            <Input
              value={draft.featured_title || ""}
              onChange={(e) => set("featured_title", e.target.value)}
              data-testid="featured-title"
            />
          </div>
        </div>
      </section>

      {/* STORY */}
      <section>
        <h2 className="font-serif text-3xl mb-4">Story / craft band</h2>
        <div className="space-y-4">
          <div>
            <Label>Overline</Label>
            <Input
              value={draft.story_overline || ""}
              onChange={(e) => set("story_overline", e.target.value)}
              data-testid="story-overline"
            />
          </div>
          <div>
            <Label>Title lines</Label>
            <div className="space-y-2">
              {(draft.story_title_lines || []).map((l, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    value={l}
                    onChange={(e) => updateStoryLine(i, e.target.value)}
                    data-testid={`story-line-${i}`}
                  />
                  <button onClick={() => removeStoryLine(i)} className="text-[#8b3a36]">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button onClick={addStoryLine} className="text-xs uppercase tracking-widest text-[#c58b76]">
                + Add line
              </button>
            </div>
          </div>
          <div>
            <Label>Body</Label>
            <Textarea
              rows={4}
              value={draft.story_body || ""}
              onChange={(e) => set("story_body", e.target.value)}
              data-testid="story-body"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>CTA label</Label>
              <Input
                value={draft.story_cta || ""}
                onChange={(e) => set("story_cta", e.target.value)}
                data-testid="story-cta-input"
              />
            </div>
            <div>
              <Label>Story image URL</Label>
              <Input
                value={draft.story_image || ""}
                onChange={(e) => set("story_image", e.target.value)}
                data-testid="story-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <section>
        <h2 className="font-serif text-3xl mb-4">Footer</h2>
        <Label>Tagline</Label>
        <Textarea
          rows={3}
          value={draft.footer_tagline || ""}
          onChange={(e) => set("footer_tagline", e.target.value)}
          data-testid="footer-tagline"
        />
      </section>

      {/* PRODUCT PAGE PERKS */}
      <section>
        <h2 className="font-serif text-3xl mb-2">Product page perks</h2>
        <p className="text-sm text-[#5c5a56] mb-6">
          The small badges shown on every product page next to the price. Toggle each one
          on / off and edit its label.
        </p>

        <div className="border border-[#ebe5df] p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl">Shipping badge</h3>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.shipping_badge_enabled !== false}
                onChange={(e) => set("shipping_badge_enabled", e.target.checked)}
                data-testid="shipping-badge-toggle"
              />
              Show
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Heading</Label>
              <Input
                value={draft.shipping_label || ""}
                onChange={(e) => set("shipping_label", e.target.value)}
                data-testid="shipping-label"
              />
            </div>
            <div>
              <Label>Sub-text</Label>
              <Input
                value={draft.shipping_sublabel || ""}
                onChange={(e) => set("shipping_sublabel", e.target.value)}
                data-testid="shipping-sublabel"
              />
            </div>
          </div>
        </div>

        <div className="border border-[#ebe5df] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl">Returns badge</h3>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.returns_badge_enabled !== false}
                onChange={(e) => set("returns_badge_enabled", e.target.checked)}
                data-testid="returns-badge-toggle"
              />
              Show
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Heading</Label>
              <Input
                value={draft.returns_label || ""}
                onChange={(e) => set("returns_label", e.target.value)}
                data-testid="returns-label"
              />
            </div>
            <div>
              <Label>Sub-text</Label>
              <Input
                value={draft.returns_sublabel || ""}
                onChange={(e) => set("returns_sublabel", e.target.value)}
                data-testid="returns-sublabel"
              />
            </div>
          </div>
        </div>

        <p className="text-[11px] text-[#5c5a56] mt-4">
          The Warranty text shown alongside these badges is set per-product, in the
          Products tab → Warranty field.
        </p>
      </section>

      <div className="sticky bottom-0 bg-white border-t border-[#ebe5df] py-4 -mx-6 px-6 lg:-mx-10 lg:px-10 flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => setDraft(content)}
          className="rounded-none"
          data-testid="content-reset"
        >
          Reset
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-none"
          data-testid="content-save"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: "",
    type: "percent",
    value: 10,
    min_order_amount: 0,
    max_discount: "",
    max_uses: "",
    first_order_only: false,
    expires_at: "",
    description: "",
    active: true,
  });

  const reload = () => api.get("/coupons").then((r) => setCoupons(r.data));
  useEffect(() => {
    reload();
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({
      code: "",
      type: "percent",
      value: 10,
      min_order_amount: 0,
      max_discount: "",
      max_uses: "",
      first_order_only: false,
      expires_at: "",
      description: "",
      active: true,
    });
    setOpen(true);
  };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code,
      type: c.type,
      value: c.value,
      min_order_amount: c.min_order_amount || 0,
      max_discount: c.max_discount ?? "",
      max_uses: c.max_uses ?? "",
      first_order_only: !!c.first_order_only,
      expires_at: c.expires_at || "",
      description: c.description || "",
      active: !!c.active,
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      value: parseFloat(form.value) || 0,
      min_order_amount: parseFloat(form.min_order_amount) || 0,
      max_discount: form.max_discount === "" ? null : parseFloat(form.max_discount),
      max_uses: form.max_uses === "" ? null : parseInt(form.max_uses),
      expires_at: form.expires_at || null,
    };
    try {
      if (editing) {
        await api.patch(`/coupons/${editing.id}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/coupons", payload);
        toast.success("Coupon created");
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (c) => {
    await api.patch(`/coupons/${c.id}`, { active: !c.active });
    reload();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await api.delete(`/coupons/${c.id}`);
    reload();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-serif text-3xl">Coupons</h2>
          <p className="text-sm text-[#5c5a56] mt-1">
            Promotional codes · {coupons.length} total · {coupons.filter((c) => c.active).length} active
          </p>
        </div>
        <Button onClick={startCreate} className="rounded-none" data-testid="new-coupon-btn">
          <Plus size={14} className="mr-1.5" /> New coupon
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Conditions</TableHead>
            <TableHead>Used</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {coupons.map((c) => (
            <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
              <TableCell>
                <div className="font-serif text-lg tracking-wider">{c.code}</div>
                <div className="text-[11px] text-[#5c5a56]">{c.description}</div>
              </TableCell>
              <TableCell>
                {c.type === "percent" ? `${c.value}%` : `₹${c.value}`}
                {c.max_discount ? ` · max ₹${c.max_discount}` : ""}
              </TableCell>
              <TableCell className="text-sm">
                {c.min_order_amount ? <div>Min ₹{c.min_order_amount}</div> : <div>No minimum</div>}
                {c.first_order_only && <div className="text-[#c58b76]">First order only</div>}
                {c.expires_at && <div>Expires {new Date(c.expires_at).toLocaleDateString()}</div>}
              </TableCell>
              <TableCell>
                {c.used_count || 0}
                {c.max_uses != null && ` / ${c.max_uses}`}
              </TableCell>
              <TableCell>
                <button
                  onClick={() => toggle(c)}
                  className={`text-[10px] tracking-widest uppercase px-2 py-1 ${
                    c.active ? "bg-[#365e47] text-white" : "bg-[#ebe5df] text-[#5c5a56]"
                  }`}
                  data-testid={`toggle-${c.code}`}
                >
                  {c.active ? "active" : "off"}
                </button>
              </TableCell>
              <TableCell className="text-right">
                <button onClick={() => startEdit(c)} className="text-xs uppercase tracking-wider mr-3 hover:text-[#c58b76]" data-testid={`edit-coupon-${c.code}`}>
                  Edit
                </button>
                <button onClick={() => remove(c)} className="text-[#8b3a36]" data-testid={`delete-coupon-${c.code}`}>
                  <Trash2 size={16} />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit coupon" : "New coupon"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code (uppercase)</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                data-testid="coupon-form-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="coupon-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value ({form.type === "percent" ? "%" : "₹"})</Label>
                <Input
                  type="number"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  data-testid="coupon-form-value"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min order ₹</Label>
                <Input
                  type="number"
                  value={form.min_order_amount}
                  onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                />
              </div>
              <div>
                <Label>Max discount ₹ (optional)</Label>
                <Input
                  type="number"
                  value={form.max_discount}
                  onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max total uses (optional)</Label>
                <Input
                  type="number"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                />
              </div>
              <div>
                <Label>Expires (optional)</Label>
                <Input
                  type="date"
                  value={form.expires_at ? form.expires_at.slice(0, 10) : ""}
                  onChange={(e) =>
                    setForm({ ...form, expires_at: e.target.value ? `${e.target.value}T23:59:59Z` : "" })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Description (shown to customers)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="flex gap-6 items-center pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.first_order_only}
                  onChange={(e) => setForm({ ...form, first_order_only: e.target.checked })}
                  data-testid="coupon-form-first"
                />
                First order only
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Active
              </label>
            </div>
            <Button onClick={save} className="w-full rounded-none" data-testid="coupon-form-save">
              {editing ? "Save changes" : "Create coupon"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  return (
    <main className="bg-[#fdfbf7] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="overline">Atelier control room</div>
        <h1 className="font-serif text-5xl mt-3 mb-10">Administration</h1>

        <Tabs defaultValue="orders">
          <TabsList className="bg-transparent border-b border-[#ebe5df] rounded-none w-full justify-start flex-wrap h-auto">
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none">Orders</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none">Customers</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none">Products</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="rounded-none">Home Content</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons" className="rounded-none">Coupons</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts" className="rounded-none">Discounts</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications" className="rounded-none">Notifications</TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-staff" className="rounded-none">Staff</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai" className="rounded-none">AI Concierge</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-8">
            <OrdersTab />
          </TabsContent>
          <TabsContent value="customers" className="mt-8">
            <CustomersTab />
          </TabsContent>
          <TabsContent value="products" className="mt-8">
            <ProductsTab />
          </TabsContent>
          <TabsContent value="content" className="mt-8">
            <ContentTab />
          </TabsContent>
          <TabsContent value="coupons" className="mt-8">
            <CouponsTab />
          </TabsContent>
          <TabsContent value="discounts" className="mt-8">
            <DiscountsTab />
          </TabsContent>
          <TabsContent value="notifications" className="mt-8">
            <NotificationsTab />
          </TabsContent>
          <TabsContent value="staff" className="mt-8">
            <StaffTab />
          </TabsContent>
          <TabsContent value="ai" className="mt-8">
            <ChatConfigTab />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
